import { CompareArrows, Undo } from "@mui/icons-material";
import { Divider, Grid, Paper, Typography } from "@mui/material";
import { DataType } from "ka-table";
import { Column } from "ka-table/models";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ConfirmationDialog } from "../../../App/Components/ConfirmationDialog/ConfirmationDialog";
import CRMMenu from "../../../App/Components/Menu/CRMMenu";
import Breadcrumbs from "../../../App/Components/Navigation/Subcomponents/Breadcrumbs/Breadcrumbs";
import { PageContainer } from "../../../App/Components/PageContainer/PageContainer";
import { PageTitle } from "../../../App/Components/PageTitle/PageTitle";
import CrmTable from "../../../App/Components/Table/CrmTable";
import { CandidateImportSource } from "../../../App/Enums/CandidateEnums";
import {
  useCandidateHistoryQuery,
  useCandidateProfileQuery,
  useRollbackCandidate,
} from "../../../App/Queries/CandidateQueries";
import { useManagementClients } from "../../../App/Queries/ClientQueries";
import { useNotification } from "../../../App/State/Hooks/notificationHooks";
import { useUserProfileStore } from "../../../App/State/Stores/UserProfileStore";
import { ICandidateHistory } from "../../../App/Typings/CandidateProfile";
import CandidateDiffModal from "./CandidateDiffModal";

const columnNames: Column[] = [
  { title: "Date Added", key: "createdDate", dataType: DataType.Date },
  { title: "Name", key: "fullName", dataType: DataType.String },
  { title: "Change Source", key: "changeSource", dataType: DataType.String },
  { title: "Company", key: "company", dataType: DataType.String, visible: false },
  { title: "Title", key: "title", dataType: DataType.String, visible: false },
  { title: "Phone Numbers", key: "phoneNumbers", dataType: DataType.Object },
  { title: "Email Addresses", key: "emailAddresses", dataType: DataType.Object },
  { title: "Last Updated", key: "lastUpdatedDate", dataType: DataType.Date },
  { title: "Latest Interacted", key: "lastInteractedWithDate", dataType: DataType.Date },
  {
    title: "Actions",
    key: "actions",
    dataType: DataType.Object,
    isEditable: false,
    isSortable: false,
    isFilterable: false,
    width: 100,
  },
];

function ViewCandidate() {
  const { id } = useParams();
  const { data: candidate, isLoading, error } = useCandidateProfileQuery(id ?? "");
  const navigate = useNavigate();
  const { data: clients, isLoading: isLoadingManagementClients } = useManagementClients();
  const { getUserAdjustedDateFromNow, getUserAdjustedDate } = useUserProfileStore();
  const { data: candidateHistory, isLoading: loadingCandidateHistory } = useCandidateHistoryQuery(id ?? "");
  const { mutateAsync: rollbackCandidate, isLoading: rollbackLoading } = useRollbackCandidate();
  const { displaySuccess, displayError } = useNotification();

  const [open, setOpen] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<ICandidateHistory | undefined>(undefined);

  useEffect(() => {
    if (!isLoadingManagementClients && (!clients || clients.length <= 0)) {
      navigate("/");
    }
  }, [isLoadingManagementClients, clients, navigate]);

  const rollback = async (candidate: ICandidateHistory) => {
    await rollbackCandidate(
      {
        userId: "",
        candidateId: candidate.candidateId,
        historyId: candidate.id,
      },
      {
        onSuccess: () => {
          displaySuccess("Candidate updated.");
          setSelectedCandidate(undefined);
          setConfirmationOpen(false);
          window.location.reload();
        },
        onError: () => {
          displayError("There was an error rolling back the candidate.");
          setSelectedCandidate(undefined);
          setConfirmationOpen(false);
        },
      },
    );
  };

  return isLoading ? (
    error ? (
      <div>There was an error loading the candidate</div>
    ) : (
      <div>Loading...</div>
    )
  ) : (
    <>
      <PageContainer>
        <Breadcrumbs pageTitle="Candidates" />
        <PageTitle>Candidate Profile</PageTitle>
        <Paper sx={{ mt: { xs: 1, md: 2 }, p: { xs: 2, lg: 4 } }}>
          <Grid container rowSpacing={2}>
            <Grid item xs={12} md={3}>
              <Typography fontWeight="bold">Name:</Typography>
              <Typography data-test="candidate-name">
                {candidate?.firstName} {candidate?.lastName}
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography fontWeight="bold">Title:</Typography>
              <Typography data-test="candidate-title">{candidate?.title}</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography fontWeight="bold">Company:</Typography>
              <Typography data-test="candidate-company">{candidate?.company}</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography fontWeight="bold">Emails:</Typography>
              <Typography data-test="candidate-emails">
                {candidate?.emailAddresses.map((email) => email.emailAddress).join(", ")}
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography fontWeight="bold">Phone Numbers:</Typography>
              <Typography data-test="candidate-phone-numbers">
                {candidate?.phoneNumbers.map((phone) => phone.phoneNumber).join(", ")}
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography fontWeight="bold">Locations:</Typography>
              <Typography data-test="candidate-locations">
                {candidate?.locations
                  .map(
                    (location) =>
                      `${location.name} (${location?.coordinates?.lat}, ${location?.coordinates?.lon})`,
                  )
                  .join(", ")}
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography fontWeight="bold">Tags:</Typography>
              <Typography data-test="candidate-tags">{candidate?.tags.join(", ")}</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography fontWeight="bold">Created:</Typography>
              <Typography data-test="candidate-created-date">
                {candidate?.createdDate
                  ? `${getUserAdjustedDate(candidate.createdDate).format("MM/DD/YYYY h:mm A")} (${getUserAdjustedDateFromNow(candidate.createdDate)})`
                  : ""}
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography fontWeight="bold">Last Updated:</Typography>
              <Typography data-test="candidate-last-updated-date">
                {candidate?.updatedDate
                  ? `${getUserAdjustedDate(candidate.updatedDate).format("MM/DD/YYYY h:mm A")} (${getUserAdjustedDateFromNow(candidate.updatedDate)})`
                  : "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography fontWeight="bold">Last Interacted:</Typography>
              <Typography data-test="candidate-last-interacted-date">
                {candidate?.lastInteractedWithDate
                  ? `${getUserAdjustedDate(candidate.lastInteractedWithDate).format("MM/DD/YYYY h:mm A")} (${getUserAdjustedDateFromNow(candidate.lastInteractedWithDate)})`
                  : "N/A"}
              </Typography>
            </Grid>
          </Grid>
          <Divider sx={{ mt: 5 }} />
          <Grid container>
            <Grid item xs={12}>
              <h2>History</h2>
            </Grid>
            <Grid item xs={12}>
              <CrmTable
                loading={loadingCandidateHistory}
                name={`candidate-${id}-history-admin`}
                data={
                  candidateHistory?.map((c) => {
                    return {
                      ...c.changedCandidate,
                      id: c.id,
                      changeSource: getSourceLabel(c.changeSource),
                      actions: (
                        <CRMMenu
                          id={`candidate-history-actions-${c.id}`}
                          title="Actions"
                          links={[
                            {
                              title: "View Diff",
                              onClick: () => {
                                setSelectedCandidate(c);
                                setOpen(true);
                              },
                              tooltip: "View changes",
                              dataTestId: "candidate-history-view-diff",
                              icon: <CompareArrows />,
                            },
                            {
                              title: "Rollback",
                              onClick: () => {
                                setSelectedCandidate(c);
                                setConfirmationOpen(true);
                              },
                              tooltip: "Rollback",
                              dataTestId: "candidate-history-rollback",
                              icon: <Undo />,
                            },
                          ]}
                        />
                      ),
                    };
                  }) ?? []
                }
                onRowClick={(row) => {
                  setSelectedCandidate(candidateHistory?.find((c) => c.id === row.id));
                  setOpen(true);
                }}
                columns={columnNames}
                pagination={{
                  enabled: false,
                  rowsPerPage: [10, 25, 50],
                  currentPage: 0,
                  rows: 10,
                }}
              />
            </Grid>
          </Grid>
        </Paper>
      </PageContainer>
      {candidate && selectedCandidate && (
        <>
          <CandidateDiffModal
            onClose={() => setOpen(false)}
            isOpen={open}
            candidate={candidate}
            selectedCandidate={selectedCandidate}
            onRollbackClick={(history) => {
              setSelectedCandidate(history);
              setConfirmationOpen(true);
            }}
          />
          <ConfirmationDialog
            isOpen={confirmationOpen}
            title="Rollback Candidate"
            message="Are you sure you want to rollback this candidate?"
            handleConfirm={async () => await rollback(selectedCandidate)}
            handleCancel={() => setConfirmationOpen(false)}
            confirmMessage={rollbackLoading ? "Saving" : "Rollback"}
            handleClose={() => setConfirmationOpen(false)}
            cancelMessage="Cancel"
            buttonsDisabled={rollbackLoading}
          />
        </>
      )}
    </>
  );
}

const getSourceLabel = (source: CandidateImportSource) => {
  return source === CandidateImportSource.Manual ? "Manually added" : CandidateImportSource[source];
};

export default ViewCandidate;
